import type React from "react";
import { type Charity, centsToDollars, type FormData } from "../types";

interface TotalAmountSectionProps {
	formData: FormData;
	charities: Charity[];
	exceedsLimit: boolean;
}

export const TotalAmountSection: React.FC<TotalAmountSectionProps> = ({
	formData,
	charities,
	exceedsLimit,
}) => {
	const buildBasicAllocationTable = () => {
		const {
			basicDonationAmountCents,
			tipAmountCents,
			tipType,
			tipSize,
			recurring,
		} = formData;
		const isMonthly = recurring;
		const amplifyPercentage = tipType === "percentage" ? `(+${tipSize}%)` : "";

		const basicDonationAmount = centsToDollars(basicDonationAmountCents);
		const tipDollarAmount = centsToDollars(tipAmountCents);
		const combinedTotal = basicDonationAmount + tipDollarAmount;

		return (
			<table>
				<tbody>
					<tr>
						<td>
							<h2 className="text-lg mt-8">Summary</h2>
						</td>
						<td>
							<h2 className="text-lg mt-8">Amount</h2>
						</td>
					</tr>
					<tr>
						<td>The most effective charities</td>
						<td>{basicDonationAmount} HKD</td>
					</tr>
					<tr>
						<td>Help grow GiveWise's work {amplifyPercentage}</td>
						<td>{tipDollarAmount} HKD</td>
					</tr>
					<tr>
						<td>
							<div className="total-donation">
								TOTAL DONATION {isMonthly ? "(each month)" : ""}
							</div>
						</td>
						<td>{combinedTotal} HKD</td>
					</tr>
				</tbody>
			</table>
		);
	};

	const buildSpecificAllocationTable = () => {
		const {
			specificAllocationsCents,
			tipAmountCents,
			tipType,
			tipSize,
			recurring,
		} = formData;
		const isMonthly = recurring;

		const amplifyPercentage = tipType === "percentage" ? `(${tipSize}%)` : "";

		// Create a lookup table for charity names
		const charityNameLookup: Record<string, string> = {};
		charities.forEach((charity) => {
			charityNameLookup[charity.id.toString()] = charity.name;
		});

		// Calculate combined total in dollars for display
		let combinedTotalCents = 0;
		for (const charity in specificAllocationsCents) {
			combinedTotalCents += specificAllocationsCents[charity] || 0;
		}
		combinedTotalCents = combinedTotalCents + tipAmountCents;
		const combinedTotal = centsToDollars(combinedTotalCents);

		return (
			<table>
				<tbody>
					<tr>
						<td>
							<h2 className="text-lg mt-8">Summary</h2>
						</td>
						<td>
							<h2 className="text-lg mt-8">Amount</h2>
						</td>
					</tr>
					{Object.entries(specificAllocationsCents).map(
						([charitySlug, amountCents]) => {
							const charityAmountCents = amountCents || 0;
							if (charityAmountCents === 0) return null;
							const charityAmount = centsToDollars(charityAmountCents);
							return (
								<tr key={charitySlug}>
									<td>{charityNameLookup[charitySlug]}</td>
									<td>{charityAmount} HKD</td>
								</tr>
							);
						},
					)}
					<tr>
						<td>GiveWise {amplifyPercentage}</td>
						<td>{centsToDollars(tipAmountCents)} HKD</td>
					</tr>
					<tr>
						<td>
							<div className="total-donation">
								TOTAL DONATION {isMonthly ? "(each month)" : ""}
							</div>
						</td>
						<td>{combinedTotal} HKD</td>
					</tr>
				</tbody>
			</table>
		);
	};

	return (
		<section id="total-amount-section" className="total-amount-section">
			{formData.allocationType === "specific"
				? buildSpecificAllocationTable()
				: buildBasicAllocationTable()}
			{exceedsLimit && (
				<p style={{ color: "red", marginTop: "10px" }}>
					Contact info@givewise.org.hk for donations exceeding 150,000 HKD.
				</p>
			)}
		</section>
	);
};
